import React from 'react';
import { Button, Card, Badge } from 'react-bootstrap';
import { GoX } from 'react-icons/go';

/**
 * DependencyPanel — right-docked detail panel for a selected depends_on edge.
 *
 * An edge is { element_id?, from_id, to_id, rev? } where from_id is the dependent cell and to_id is
 * the prerequisite cell — i.e. "from_id depends on to_id". The panel resolves each endpoint's title
 * from the cell list and states the relationship. It mirrors CellPanel's structure and classes (close
 * button, Card.Text as="h6" headers, <hr/>, the `.properties` table) so it renders identically with no
 * panel-specific CSS.
 *
 * Deleting a dependency is done from the graph itself (the × on the edge, or the Delete key on a
 * selected edge), not from this panel.
 *
 * Props:
 *   edge    — selected edge { element_id?, from_id, to_id, rev? }
 *   cells   — array of cell vertex objects (for title resolution)
 *   onClose — () => void; deselect (falls back to the Mission panel)
 */

// Resolve a cell's display title from the cell list, falling back to its id.
function titleOf(cells, id) {
  const cell = (cells || []).find(c => c.element_id === id);
  return (cell && cell.props && cell.props.title) || id;
}

function DependencyPanel({ edge, cells, onClose }) {
  if (!edge) return null;

  const dependent = titleOf(cells, edge.from_id);
  const prerequisite = titleOf(cells, edge.to_id);

  return (
    <>
      {onClose && (
        <span className="workflow-controls-pane-right">
          <Button variant="light" onClick={onClose} aria-label="Close panel"><GoX /></Button>
        </span>
      )}

      {/* Static panel header */}
      <Card.Text as="h6">Dependency</Card.Text>
      <hr />

      {/* Type / endpoints as the platform's gray-chip table. The edge type renders as the
          design-system outlined pill. */}
      <Card.Text>
        <table className="properties">
          <tbody>
            <tr>
              <td className="prop-name-column">Type</td>
              <td>
                <Badge variant="secondary" className="title-tag-badge">depends_on</Badge>
              </td>
            </tr>
            <tr>
              <td className="prop-name-column">Dependent</td>
              <td className="prop-value-column">{dependent}</td>
            </tr>
            <tr>
              <td className="prop-name-column">Prerequisite</td>
              <td className="prop-value-column">{prerequisite}</td>
            </tr>
          </tbody>
        </table>
      </Card.Text>
    </>
  );
}

export default DependencyPanel;
